/**
 * JourneyViewPage — Goal-centric workflow visualization (DRR-309)
 *
 * Displays a single workflow instance as a journey with:
 * - Subject name + goal name (from workflow displayName)
 * - Progress visualization (tracks, percentage)
 * - Next action CTA with regulatory context
 * - Completion celebration
 *
 * Route: /ferp-core/journey/:instanceId
 * Layer: user (all staff)
 * Scope: task:read, instance:read (DRR-102)
 *
 * DRR-055: Dumb frontend — all labels from backend config.
 * DRR-309: Goal-centric, domain-agnostic workflow UI.
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Stack, Button, Group, Breadcrumbs, Anchor, Text } from '@mantine/core';
import { IconArrowLeft, IconAlertCircle } from '@tabler/icons-react';
import { JourneyView, JourneyOverlayProvider } from '@ferp/workflow-forms';

export function JourneyViewPage() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();

  if (!instanceId) {
    return (
      <Stack gap="md" p="md">
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          No instance ID provided. Please select a workflow instance.
        </Alert>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/ferp-core/instances')}
        >
          Back to Instances
        </Button>
      </Stack>
    );
  }

  const handleBack = () => {
    navigate(-1);
  };

  const handleTaskClick = (taskId: string) => {
    navigate(`/ferp-core/tasks/${taskId}`);
  };

  const handleSpawnJourney = (newInstanceId: string) => {
    navigate(`/ferp-core/journey/${newInstanceId}`);
  };

  return (
    <Stack gap="md" p="md">
      <Group justify="space-between" align="center">
        <Breadcrumbs>
          <Anchor onClick={() => navigate('/ferp-core/instances')} size="sm">
            Instances
          </Anchor>
          <Text size="sm" c="dimmed">
            Journey View
          </Text>
        </Breadcrumbs>
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconArrowLeft size={14} />}
          onClick={handleBack}
        >
          Back
        </Button>
      </Group>

      <JourneyOverlayProvider
        instanceId={instanceId}
        defaultOverlays={['OfflineStatus', 'EscalationTimer', 'AIAssistant']}
        lifeSafety
      >
        <JourneyView
          instanceId={instanceId}
          onTaskClick={handleTaskClick}
          onSpawnSuccess={handleSpawnJourney}
        />
      </JourneyOverlayProvider>
    </Stack>
  );
}

export default JourneyViewPage;
